"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { Button } from "@/shared/ui/buttons/button";
import { Link } from "@/i18n/navigation";
import { extractApiErrorMessage } from "@/shared/api/error-handler";
import type { LfgLftPostResponse } from "@/shared/api/lfg-lft-types";
import { useUserProfile } from "@/hooks/useAuth";
import { useCreateLfgLftApplication, useLfgLftApplicationsOutgoing } from "@/hooks/useLfgLft";
import { LFG_ROLES, isLfgRole, type LfgRole } from "../model/board-filters";
import { ApplyButton } from "./apply-button";
import { RespondDialog, type RespondTarget } from "./respond-dialog";
import { BoardInviteDialog } from "./board-invite-dialog";
import { useManagedTeams } from "../api/use-managed-teams";

const isTeamPost = (p: LfgLftPostResponse) => p.type === "Lft";

/** Позиции, на которые можно откликнуться: только линии (запасной и тренер — через доску). */
const LANES: readonly LfgRole[] = LFG_ROLES.filter((r) => r !== "Autofill" && r !== "Substitute" && r !== "Coach");

/**
 * Действия у чужой карточки объявления в ленте чата (макет чата): «Откликнуться» открывает отклик
 * прямо здесь, «Открыть» ведёт на доску. Отклик — только на объявление команды, в которой меня нет
 * и на которое я ещё не откликался; пригласить игрока в свою команду — на доске.
 */
export function LfgChatCardActions({ post }: { post: LfgLftPostResponse }) {
  const t = useTranslations("LfgBoard");
  const { data: me } = useUserProfile();
  const outgoing = useLfgLftApplicationsOutgoing(!!me?.id);
  const create = useCreateLfgLftApplication();
  const [target, setTarget] = useState<RespondTarget | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const managed = useManagedTeams(!isTeamPost(post) && me?.id ? me.id : undefined).data?.managed ?? [];

  const isTeam = post.type === "Lft";
  const mine = !!me?.id && (post.author?.id === me.id || post.rosterSlots?.some((s) => s.userId === me.id));
  const applied = (outgoing.data ?? []).some((a) => a.postId === post.id && (a.status === "Pending" || a.status === "Accepted"));
  const lanes = (post.roles ?? [post.role]).filter(isLfgRole).filter((r) => LANES.includes(r));
  const canApply = isTeam && !!me?.id && !mine && !applied && lanes.length > 0;
  // Игрока зовут в свою основную команду — как на доске; арам-объявления игрока ведут себя так же.
  const canInvite = !isTeam && !!me?.id && !mine && !applied && managed.length > 0;

  return (
    <span className="flex flex-wrap items-center gap-2">
      {canApply && (
        <ApplyButton
          lanes={lanes}
          disabled={create.isPending}
          onApply={(role) => setTarget({ postId: post.id, role, toName: post.author?.displayName ?? "" })}
        />
      )}
      {canInvite && (
        <Button
          variant="outline"
          disabled={create.isPending}
          onClick={() => setInviteOpen(true)}
          className="h-[34px] border-highlight-border bg-highlight-soft px-3.5 font-bold text-highlight hover:bg-highlight-soft/80 hover:text-highlight"
        >
          {t("chatCard.invite")}
        </Button>
      )}
      {applied && <span className="text-xs text-chat-muted-fg">{t("card.applied")}</span>}
      <Button asChild variant="outline" className="h-[34px]">
        <Link href={{ pathname: "/lfg", query: { post: post.id } }}>{t("chatCard.openShort")}</Link>
      </Button>
      {canInvite && (
        <BoardInviteDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          teams={managed}
          onInvite={(sourceTeamId, message) =>
            create.mutate(
              { postId: post.id, body: { targetRole: "", sourceTeamId, message: message.trim() || null } },
              { onSuccess: () => setInviteOpen(false), onError: (e) => toast.error(extractApiErrorMessage(e, t("write.error"))) },
            )
          }
        />
      )}
      <RespondDialog
        target={target}
        pending={create.isPending}
        onCancel={() => setTarget(null)}
        onSend={(tg, message) =>
          create.mutate(
            { postId: tg.postId, body: { targetRole: tg.role, message: message.trim() || null } },
            { onSuccess: () => setTarget(null), onError: (e) => toast.error(extractApiErrorMessage(e, t("write.error"))) },
          )
        }
      />
    </span>
  );
}
